import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from './icons';
import { cx, FOCUS_RING } from './styles';

/**
 * Replaces the hand-rolled `flex items-end justify-between` header that all ten
 * v1 pages repeat. Exactly one `<h1>` per screen lives here.
 *
 * `lg`+: title + subtitle left, `actions` right-aligned on the same row, `meta`
 * under the subtitle. Below `lg`: title/subtitle, then `actions` on their own
 * full-width row (`flex-1 min-w-[140px]` each — nothing is squeezed and no
 * action is hidden), then `meta`.
 */
export interface Crumb {
  label: string;
  /** Omit on the trailing crumb — the current page is not a link. */
  to?: string;
}

export interface PageHeaderProps {
  title: string;
  /** Rendered beside the title, e.g. a `Beta` badge. */
  badge?: React.ReactNode;
  subtitle?: React.ReactNode;
  breadcrumb?: Crumb[];
  actions?: React.ReactNode;
  /** Result counts, e.g. `Showing X of Y tickets`. */
  meta?: React.ReactNode;
  className?: string;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  badge,
  subtitle,
  breadcrumb,
  actions,
  meta,
  className,
}) => (
  <header data-tour="page-header" className={cx('mb-6 lg:mb-8', className)}>
    {breadcrumb && breadcrumb.length > 0 && (
      <nav aria-label="Breadcrumb" className="mb-3">
        <ol className="flex flex-wrap items-center gap-1 text-xs text-white/40">
          {breadcrumb.map((crumb, i) => (
            <li key={`${crumb.label}-${i}`} className="flex items-center gap-1">
              {i > 0 && <ChevronRightIcon className="h-3 w-3 text-white/25" />}
              {crumb.to ? (
                <Link
                  to={crumb.to}
                  className={cx('rounded transition-colors hover:text-white/70', FOCUS_RING)}
                >
                  {crumb.label}
                </Link>
              ) : (
                <span aria-current="page" className="text-white/60">
                  {crumb.label}
                </span>
              )}
            </li>
          ))}
        </ol>
      </nav>
    )}

    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between lg:gap-6">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="text-2xl font-bold tracking-tight text-white lg:text-[28px]">{title}</h1>
          {badge}
        </div>
        {subtitle && <p className="mt-1.5 max-w-2xl text-sm text-white/50">{subtitle}</p>}
        {meta && <div className="mt-3 hidden text-xs text-white/40 lg:block">{meta}</div>}
      </div>

      {actions && (
        <div
          className={cx(
            'flex flex-wrap items-center gap-2',
            '[&>*]:min-w-[140px] [&>*]:flex-1',
            'lg:flex-nowrap lg:justify-end lg:[&>*]:min-w-0 lg:[&>*]:flex-none',
          )}
        >
          {actions}
        </div>
      )}
    </div>

    {meta && <div className="mt-4 text-xs text-white/40 lg:hidden">{meta}</div>}
  </header>
);

/** A `w-px h-6 bg-white/10` divider for separating a view switch from actions. */
export const ActionDivider: React.FC = () => (
  <div aria-hidden="true" className="hidden h-6 w-px flex-none bg-white/10 lg:block" />
);
